import type { Signal } from '@angular/core'
import { computed } from '@angular/core'
import { Injectable, inject } from '@angular/core'
import { EntitySignalQuery } from '@awork/core/state/signal-store/entitySignalQuery'
import type { ProjectStatus } from '@awork/features/project/models/project-status.model'
import { ProjectStatusStore } from '@awork/features/project/state/project-status.store'

@Injectable({ providedIn: 'root' })
export class ProjectStatusQuery extends EntitySignalQuery<ProjectStatus> {
  protected store: ProjectStatusStore

  constructor() {
    const store = inject(ProjectStatusStore)

    super(store)

    this.store = store
  }

  /**
   * Gets a project status by id
   * @param {string} projectStatusId
   * @returns {Signal<ProjectStatus | null>}
   */
  queryProjectStatus(projectStatusId: string): Signal<ProjectStatus | null> {
    return this.queryEntity(projectStatusId)
  }

  /**
   * Gets the project statuses of a project
   * @param {string} projectId
   * @returns {Signal<ProjectStatus[]>}
   */
  queryProjectStatusesByProjectId(projectId: string): Signal<ProjectStatus[]> {
    return this.queryAll({
      filterBy: projectStatus => projectStatus.projectId === projectId,
      sortBy: this.sortStatuses
    })
  }

  /**
   * Gets the project statuses for a project, falling back to workflow statuses when applicable.
   * @param {string} projectId The project id
   * @param {string} workflowId The linked workflow id
   * @returns {Signal<ProjectStatus[]>}
   */
  queryProjectStatusesForProject(projectId: string, workflowId?: string): Signal<ProjectStatus[]> {
    return computed(() => {
      const projectStatuses = this.queryProjectStatusesByProjectId(projectId)
      const statuses = projectStatuses()

      if (statuses.length || !workflowId) {
        return statuses
      }

      const workflowStatuses = this.queryProjectStatusesByWorkflowId(workflowId)

      return workflowStatuses()
    })
  }

  /**
   * Gets the project statuses of a project template
   * @param {string} projectTemplateId
   * @returns {Signal<ProjectStatus[]>}
   */
  queryProjectStatusesByProjectTemplateId(projectTemplateId: string): Signal<ProjectStatus[]> {
    return this.queryAll({
      filterBy: projectStatus => projectStatus.projectTemplateId === projectTemplateId,
      sortBy: this.sortStatuses
    })
  }

  /**
   * Gets the project statuses of a workflow
   * @param {string} workflowId
   * @returns {Signal<ProjectStatus[]>}
   */
  queryProjectStatusesByWorkflowId(workflowId: string): Signal<ProjectStatus[]> {
    return this.queryAll({
      filterBy: projectStatus => projectStatus.workflowId === workflowId,
      sortBy: this.sortStatuses
    })
  }

  /**
   * Gets all non-preset project statuses
   * @returns {Signal<ProjectStatus[]>}
   */
  queryNonPresetProjectStatuses(): Signal<ProjectStatus[]> {
    return this.queryAll({
      filterBy: projectStatus => !projectStatus.isPreset,
      sortBy: this.sortStatuses
    })
  }

  /**
   * Sorts project statuses by order.
   * @param {ProjectStatus} a The first status
   * @param {ProjectStatus} b The second status
   * @returns {number}
   */
  private sortStatuses(a: ProjectStatus, b: ProjectStatus): number {
    return a.order - b.order
  }
}
